import React, { useState, useEffect, useRef } from "react";
import { Title, FormatBold, FormatItalic, FormatUnderlined, DriveFileRenameOutline, InsertPhoto, TableView, InsertLink, FormatListBulleted, FormatListNumbered, FormatQuote, HorizontalRule, Code, IntegrationInstructions } from "@mui/icons-material";
import { Stack, Box, Select, Typography, MenuItem, Divider, IconButton, TextField } from "@mui/material";
import MuiMarkdown from "mui-markdown";

export interface IMarkdownEditorProps {
    /** The content of the editor */
    markdown?: string;
    /** Callback on content editor change */
    onMarkdownChange?: (markdown: string) => void;
    /** Optional boolean to show the full screen of the preview, default to false */
    fullPreview?: boolean;
    /** Optional render of the full screen, if the render is different than the content only */
    renderFullPreview?: (markdown: string) => React.ReactElement;
}

export const MarkdownEditor = ({
    markdown: markdownProp,
    onMarkdownChange,
    fullPreview = false,
    renderFullPreview,
}: IMarkdownEditorProps) => {
    const [markdown, setMarkdown] = useState(markdownProp || '');
    useEffect(() => {
        if (!markdownProp) return;
        setMarkdown(markdownProp)
    }, [markdownProp])
    const inputRef = useRef<HTMLInputElement>(null);

    const handleEditorChange = (text: string) => {
        setMarkdown(text);
        onMarkdownChange?.(text);
    };

    /** Handle the formatting of the selected text in a certain header */
    const handleHeaderClick = (level: number) => {
        const input = inputRef.current;
        if (!input) return;

        const { selectionStart, selectionEnd } = input;

        if (selectionStart === null || selectionEnd === null) return;

        const headerText = markdown.slice(selectionStart, selectionEnd);
        const headerSyntax = '#'.repeat(level);

        const newText =
            markdown.slice(0, selectionStart) +
            `${headerSyntax} ${headerText}` +
            markdown.slice(selectionEnd);

        setMarkdown(newText);
    };

    /** Handle formatting of selected text in bold */
    const handleBoldClick = () => {
        const input = inputRef.current;
        if (!input) return;
        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;

        if (selectionStart === null || selectionEnd === null) return;

        const newText =
            markdown.slice(0, selectionStart) +
            `**${markdown.slice(selectionStart, selectionEnd)}**` +
            markdown.slice(selectionEnd);

        setMarkdown(newText);
    };

    /** Handle formatting of selected text in italic */
    const handleItalicClick = () => {
        const input = inputRef.current;
        if (!input) return;
        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;

        if (selectionStart === null || selectionEnd === null) return;

        const newText =
            markdown.slice(0, selectionStart) +
            `*${markdown.slice(selectionStart, selectionEnd)}*` +
            markdown.slice(selectionEnd);

        setMarkdown(newText);
    };

    /** Handle formatting of selected text in underline */
    const handleUnderlineClick = () => {
        const input = inputRef.current;
        if (!input) return;
        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;

        if (selectionStart === null || selectionEnd === null) return;

        const newText =
            markdown.slice(0, selectionStart) +
            `<u>${markdown.slice(selectionStart, selectionEnd)}</u>` +
            markdown.slice(selectionEnd);

        setMarkdown(newText);
    }

    /** Handle formatting of selected text in highlight */
    const handleHighlightClick = () => {
        const input = inputRef.current;
        if (!input) return;
        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;

        if (selectionStart === null || selectionEnd === null) return;

        const newText =
            markdown.slice(0, selectionStart) +
            `==${markdown.slice(selectionStart, selectionEnd)}==` +
            markdown.slice(selectionEnd);

        setMarkdown(newText);
    }

    /** Handle formatting of selected text with hyperlink */
    const handleLinkClick = () => {
        const input = inputRef.current;
        if (!input) return;

        const { selectionStart, selectionEnd } = input;

        if (selectionStart === null || selectionEnd === null) return;

        const linkText = markdown.slice(selectionStart, selectionEnd);
        const linkURL = prompt('Enter the URL for the link:');

        if (linkURL) {
            const newText =
                markdown.slice(0, selectionStart) +
                `[${linkText}](${linkURL})` +
                markdown.slice(selectionEnd);

            setMarkdown(newText);
        }
    };

    const handleListClick = (type?: 'bullet' | 'number') => {
        const input = inputRef.current;
        if (!input) return;

        const { selectionStart, selectionEnd } = input;

        const formatText = (text: string) => type === 'number' ? `1. ${text}` : `- ${text}`;

        if (selectionStart === null || selectionEnd === null || selectionStart === selectionEnd) {
            //if no selection just prompt for one
            const listText = prompt('Enter list item:');
            if (listText) {
                const newText = formatText(listText);
                setMarkdown(markdown + '\n' + newText);
            }
        } else {
            //if selection, transform into a list
            const listText = markdown.slice(selectionStart, selectionEnd);
            const listItems = listText.split('\n');
            const newText =
                markdown.slice(0, selectionStart) +
                listItems.map(item => formatText(item)).join('\n') +
                markdown.slice(selectionEnd);
            setMarkdown(newText);
        }
    };

    const handleImageClick = () => {
        const imageUrl = prompt('Enter image URL:');
        if (imageUrl) {
            const newText = `${markdown}![Alt Text](${imageUrl})`;
            setMarkdown(newText);
        }
    };

    /** Handle formatting of selected text into a block quote */
    const handleBlockquoteClick = () => {
        const input = inputRef.current;
        if (!input) return;

        const { selectionStart, selectionEnd } = input;

        if (selectionStart === null || selectionEnd === null || selectionStart === selectionEnd) {
            //if no selection just add from prompt
            const quoteText = prompt('Enter quote:');
            if (quoteText) {
                const newText = `${markdown}\n> ${quoteText}\n`;
                setMarkdown(markdown + newText);
            }
        } else {
            //if selection, transform into a code block
            const selectedText = markdown.slice(selectionStart, selectionEnd);
            const newText =
                markdown.slice(0, selectionStart) +
                `\n> ${selectedText}\n` +
                markdown.slice(selectionEnd);
            setMarkdown(newText);
        }
    };

    /** Handle adding an horizontal line (divider) */
    const handleHorizontalRuleClick = () => {
        const newText = `${markdown}\n---\n`;
        setMarkdown(newText);
    };

    /** Handle formatting of selected text into a code block */
    const handleCodeBlockClick = () => {
        const input = inputRef.current;
        if (!input) return;

        const { selectionStart, selectionEnd } = input;

        if (selectionStart === null || selectionEnd === null || selectionStart === selectionEnd) {
            //if no selection just add from prompt
            const codeText = prompt('Enter code block:');
            if (codeText) {
                const newText = `${markdown}\n\`\`\`\n${codeText}\n\`\`\``;
                setMarkdown(newText);
            }
        } else {
            //if selection, transform into a code block
            const selectedText = markdown.slice(selectionStart, selectionEnd);
            const newText =
                markdown.slice(0, selectionStart) +
                `\n\`\`\`\n${selectedText}\n\`\`\`` +
                markdown.slice(selectionEnd);
            setMarkdown(newText);
        }
    };

    /** Handle formatting of selected text in a inline code */
    const handleInlineCodeClick = () => {
        const input = inputRef.current;
        if (!input) return;
        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;

        if (selectionStart === null || selectionEnd === null) {
            const codeText = prompt('Enter inline code:');
            if (codeText) {
                const newText = `${markdown} \`${codeText}\``;
                setMarkdown(newText);
            }
        } else {
            const newText =
                markdown.slice(0, selectionStart) +
                `\`${markdown.slice(selectionStart, selectionEnd)}\`` +
                markdown.slice(selectionEnd);

            setMarkdown(newText);
        }
    };

    /** Handle inserting a table */
    const handleInsertTable = () => {
        const input = inputRef.current;
        if (!input) return;
        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;

        if (selectionStart === null || selectionEnd === null) return;

        if (selectionStart !== selectionEnd) {
            //if selection of text
            alert('Table will be inserted after the selection');
        }

        const tableTemplate = `
\n
| Header 1 | Header 2 |
| -- | -- |
| Content 1 | Content 2 |
\n
        `
        const newText =
            markdown.slice(0, selectionEnd) +
            tableTemplate +
            markdown.slice(selectionEnd);

        setMarkdown(newText);
    }

    return (

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {!fullPreview &&
                <Stack direction={'column'} justifyContent={'start'} alignItems={'center'} flex={1} spacing={1}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: 'fit-content',
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            color: 'text.secondary',
                            flexWrap: { xs: 'wrap', md: 'nowrap' }
                        }}
                    >
                        {/* <FormControl sx={{ m: 1, minWidth: 120 }}> */}
                        <Select
                            value={''}
                            size={'small'}
                            displayEmpty
                            renderValue={() => <Stack direction={'row'}><Title /><Typography>Headers</Typography></Stack>}
                            onChange={(event) => handleHeaderClick(+event.target.value)}
                        >
                            <MenuItem value={1}>H1</MenuItem>
                            <MenuItem value={2}>H2</MenuItem>
                            <MenuItem value={3}>H3</MenuItem>
                        </Select>
                        {/* </FormControl> */}
                        {/* Following not supported by markdown */}
                        {/* <IconButton><FormatAlignLeft /></IconButton>
                        <IconButton><FormatAlignCenter /></IconButton>
                        <IconButton><FormatAlignRight /></IconButton> */}
                        <Divider orientation="vertical" variant="middle" flexItem />
                        <IconButton onClick={handleBoldClick}><FormatBold /></IconButton>
                        <IconButton onClick={handleItalicClick}><FormatItalic /></IconButton>
                        <IconButton onClick={handleUnderlineClick}><FormatUnderlined /></IconButton>
                        <IconButton onClick={handleHighlightClick}><DriveFileRenameOutline /></IconButton>
                        <Divider orientation="vertical" variant="middle" flexItem />
                        <IconButton onClick={handleImageClick}><InsertPhoto /></IconButton>
                        <IconButton onClick={handleInsertTable}><TableView /></IconButton>
                        <IconButton onClick={handleLinkClick}><InsertLink /></IconButton>
                        <Divider orientation="vertical" variant="middle" flexItem />
                        <IconButton onClick={() => handleListClick('bullet')}><FormatListBulleted /></IconButton>
                        <IconButton onClick={() => handleListClick('number')}><FormatListNumbered /></IconButton>
                        <Divider orientation="vertical" variant="middle" flexItem />
                        <IconButton onClick={handleBlockquoteClick}><FormatQuote /></IconButton>
                        <IconButton onClick={handleHorizontalRuleClick}><HorizontalRule /></IconButton>
                        <Divider orientation="vertical" variant="middle" flexItem />
                        <IconButton onClick={handleInlineCodeClick}><Code /></IconButton>
                        <IconButton onClick={handleCodeBlockClick}><IntegrationInstructions /></IconButton>
                    </Box>
                    <TextField
                        inputRef={inputRef}
                        multiline
                        minRows={6}
                        value={markdown}
                        onChange={(e) => handleEditorChange(e.target.value)}
                        variant={"outlined"}
                        fullWidth
                    />
                </Stack>
            }
            <Box flex={1}>
                {(fullPreview && !!renderFullPreview) ?
                    renderFullPreview(markdown)
                    :
                    <MuiMarkdown >
                        {markdown}
                    </MuiMarkdown>
                }
            </Box>
        </Stack>
    );
};
